<template>
  <v-container>
    <v-layout row wrap>
      <v-flex xs12 md8 offset-md2>
        <h1 class="primary--text mb-4">New Event</h1>
        <app-alert class="mb-3" type="error" :message="error" @dismissed="error = false" />

        <v-card>
          <v-divider></v-divider>
          <v-form ref="form" @submit.prevent="doCreateAid">
            <v-container class="px-4">
              <v-layout justify-space-between row wrap>
                <v-flex xs12>
                  <v-text-field
                    label="Event Title"
                    type="text"
                    v-model="event.title"
                    hint="A title of the Event"
                    persistent-hint
                    :rules="[rules.isRequired]"
                    required
                  ></v-text-field>
                </v-flex>

                <v-flex xs12>
                  <v-textarea
                    label="Event Description"
                    type="text"
                    v-model="event.description"
                    hint="A description of the Event"
                    persistent-hint
                    :rules="[rules.isRequired]"
                    rows="2"
                    required
                  ></v-textarea>
                </v-flex>

                <v-flex xs12 sm6>
                  <v-select
                    label="State"
                    v-model="event.state_id"
                    hint="The State where this event occured"
                    persistent-hint
                    :items="stateOptions"
                    :loading="isLoading"
                    :disabled="isLoading"
                    :rules="[rules.isRequired]"
                    required
                  ></v-select>
                </v-flex>

                <v-flex xs12 sm6>
                  <v-autocomplete
                    label="Location"
                    v-model="event.location_id"
                    hint="The location where this event occured"
                    persistent-hint
                    :items="locationOptions"
                    :rules="[rules.isRequired]"
                    :loading="isLoading"
                    :disabled="isLoading"
                    no-data-text="No locations in selected state"
                    required
                  ></v-autocomplete>
                </v-flex>

                <v-flex xs12 sm6>
                  <v-autocomplete
                    label="Partner"
                    v-model="event.agency_id"
                    hint="The partner name"
                    persistent-hint
                    :items="agencyOptions"
                    :loading="isLoading"
                    :disabled="isLoading"
                    :rules="[rules.isRequired]"
                    required
                  ></v-autocomplete>
                </v-flex>

                <v-flex xs12 sm6>
                  <v-text-field
                    label="Affected Population"
                    type="number"
                    v-model="event.affected"
                    hint="An estimation of population affected"
                    persistent-hint
                    :rules="[rules.isRequired]"
                    required
                  ></v-text-field>
                </v-flex>

                <!-- Start Date Picker -->
                <v-flex xs12 sm6>
                  <v-dialog
                    ref="dateDialog"
                    v-model="dateModal"
                    :return-value.sync="event.timestamp"
                    persistent
                    lazy
                    full-width
                    width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="event.timestamp"
                        label="Date"
                        hint="The date of the Event"
                        persistent-hint
                        :rules="[rules.isRequired]"
                        readonly
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="event.timestamp"
                      :allowed-dates="allowedDates"
                      scrollable
                    >
                      <v-spacer></v-spacer>
                      <v-btn flat color="primary" @click="dateModal = false">Cancel</v-btn>
                      <v-btn flat color="primary" @click="$refs.dateDialog.save(event.timestamp)">OK</v-btn>
                    </v-date-picker>
                  </v-dialog>
                </v-flex>
              </v-layout>

              <v-layout class="mt-3 mb-2">
                <v-btn depressed color="accent" type="submit" :disabled="isSaving">
                  Create
                  <v-progress-circular
                    class="ml-2"
                    :indeterminate="true"
                    :size="18"
                    :width="2"
                    v-if="isSaving"
                  ></v-progress-circular>
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn depressed @click="$router.go(-1)">Cancel</v-btn>
              </v-layout>
            </v-container>
          </v-form>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'

import { EVENT_CREATE } from '@/store/actions/event'
import { STATE_FETCH } from '@/store/actions/state'
import { AGENCY_FETCH } from '@/store/actions/agency'
import { LOCATION_FETCH } from '@/store/actions/location'

import { progressOptions } from '@/data/options'

export default {
  name: 'CreateEvent',
  created () {
    this.isLoading = true
    this.event.agent_id = this.currentUser._id // set agent id

    Promise.all([
      this.$store.dispatch(STATE_FETCH),
      this.$store.dispatch(AGENCY_FETCH),
      this.$store.dispatch(LOCATION_FETCH)
    ]).then(() => {
      this.isLoading = false
    })
  },
  data () {
    return {
      isSaving: false,
      isLoading: false,
      statusOptions: progressOptions,
      dateModal: false,
      event: {
        timestamp: '',
        tag: '-',
        affected: '',
        title: '',
        description: '',
				status: 'unverified', // unverified
        state_id: '',
        agency_id: '',
        location_id: '',
        country_id: this.$settings.country,
        agent_id: ''
      },
      error: false,
      rules: {
        isRequired: value => this.$validator.isRequired(value)
      }
    }
  },
  computed: {
    stateOptions () {
      let stateOptions = this.$store.getters.statesAsOptions
      // If state agent, show only agents state in list
      if (this.currentUser.parent && this.currentUser.parent === 'state') {
        const state = stateOptions.find(stateOption => this.currentUser.parent_id === stateOption.value)
        stateOptions = [state]
      }
      return stateOptions
    },
    agencyOptions () {
      let agencyOptions = this.$store.getters.agenciesAsOptions
      // If agency agent, show only agents agency in list
      if (this.currentUser.parent && this.currentUser.parent === 'agency') {
        const agency = agencyOptions.find(agencyOption => this.currentUser.parent_id === agencyOption.value)
        agencyOptions = [agency]
      }
      return agencyOptions
    },
    locationOptions () {
      return this.$store.getters.locationsAsOptions(this.event.state_id)
    },
    ...mapGetters({
      currentUser: 'currentUser'
    })
  },
  methods: {
    allowedDates (date) {
      return new Date(date) <= new Date()
    },
    doCreateAid () {
      if (!this.$refs.form.validate()) return

      this.isSaving = true
      this.error = false

      const newEvent = Object.assign({}, this.event)
      newEvent.timestamp = Math.round((new Date(newEvent.timestamp)).getTime() / 1000)

      this.$store.dispatch(EVENT_CREATE, newEvent)
        .then((response) => {
          this.$router.push('/events/' + response.data + '?newEvent=1')
        })
        .catch(error => (this.error = error.message))
        .finally(() => (this.isSaving = false))
    }
  }
}
</script>
